import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../../Components/Ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../Components/Ui/select';
import { Input } from '../../Components/Ui/input';
import axios from 'axios';
import PropTypes from 'prop-types';
import { settings } from '../utils/Settings';

const AttendanceTable = ({ showLeavers = false, filters = {}, onMonthNavigation }) => {
  const [apiLoading, setApiLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [headers, setHeaders] = useState([]);
  const [students, setStudents] = useState([]);
  const [page, setPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [monthData, setMonthData] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [localSearch, setLocalSearch] = useState('');

  const [debouncedFilters, setDebouncedFilters] = useState(filters);
  const [lastFiltersHash, setLastFiltersHash] = useState('');
  
  // Symbol ↔ Number mapping for attendance codes (UI symbols mapped to API values)
  const SYMBOL_TO_NUM = { '#': '0', '/': '1', '\\': '2' };
  const NUM_TO_SYMBOL = { '0': '#', '1': '/', '2': '\\' };
  const mapToSavedValue = (val) => (val in SYMBOL_TO_NUM ? SYMBOL_TO_NUM[val] : val);
  const mapToDisplayValue = (val) => (val in NUM_TO_SYMBOL ? NUM_TO_SYMBOL[val] : val);
  
  useEffect(() => {
    const timer = setTimeout(() => {
      // Create a hash of filters to compare
      const filtersHash = JSON.stringify(filters);
      
      // Only update if filters actually changed
      if (filtersHash !== lastFiltersHash) {
        setDebouncedFilters(filters);
        setLastFiltersHash(filtersHash);
      }
    }, 500); // 500ms debounce for filter changes
    
    return () => clearTimeout(timer);
  }, [filters, lastFiltersHash]);

  const fetchAnnualAttendanceData = useCallback(async (navOverride = null) => {
    try {
      setApiLoading(true);
      setApiError(null);

      const userId = localStorage.getItem("userId");
      const roleId = localStorage.getItem("userRole");
      const schoolId = 423562;

      if (!userId || !roleId) {
        setApiError("User session expired. Please login again.");
        return;
      }

      const params = {
        "pupilAttendance.userId": userId,
        "pupilAttendance.schoolId": schoolId,
        "pupilAttendance.roleId": roleId,
        "pupilAttendance.page": page,
        "pupilAttendance.recordPerPage": recordsPerPage,
        "pupilAttendance.academicYear": "2025-26",
      };

      // filters apply karo - use debounced filters
      if (debouncedFilters.gender) params["pupilAttendance.gender"] = debouncedFilters.gender;
      // Do not send yearGroup or attendanceCode to server; filter locally to avoid disappearing rows
      // if (debouncedFilters.attendanceCode) params["pupilAttendance.attendanceCode"] = debouncedFilters.attendanceCode;
      if (debouncedFilters.localAuthority) params["pupilAttendance.localAuthority"] = debouncedFilters.localAuthority;
      if (debouncedFilters.send) params["pupilAttendance.send"] = debouncedFilters.send;
      if (debouncedFilters.eal) params["pupilAttendance.eal"] = debouncedFilters.eal;
      if (debouncedFilters.uasc) params["pupilAttendance.uasc"] = debouncedFilters.uasc;
      if (debouncedFilters.careStatus) params["pupilAttendance.careStatus"] = debouncedFilters.careStatus;
      if (debouncedFilters.subschoolId) params["pupilAttendance.subschoolId"] = debouncedFilters.subschoolId;
      if (debouncedFilters.leavers) params["pupilAttendance.leavers"] = debouncedFilters.leavers;
      if (debouncedFilters.customSearch) params["pupilAttendance.customSearch"] = debouncedFilters.customSearch;

      // month navigation - check override first, then filters
      const monthNavigation = navOverride || debouncedFilters.monthData || null;
      
      if (monthNavigation === "Previous") {
        params["pupilAttendance.monthData"] = "Previous";
        if (startDate) params["pupilAttendance.startDate"] = startDate;
      } else if (monthNavigation === "Next") {
        params["pupilAttendance.monthData"] = "Next";
        if (endDate) params["pupilAttendance.endDate"] = endDate;
      }


      const response = await axios.get(
        `${settings.API_HOST}/rest/fetchAnnualAttendanceData`,
        { 
          params,
          timeout: 30000, // 30 second timeout
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );


      const data = response?.data || {};
      const pa = data?.pupilAttendance || {};


      setTotalPages(pa.totalPages || 0);
      setTotalRecords(pa.totalRecords || 0);
      setStartDate(pa.startDate || "");
      setEndDate(pa.endDate || "");
      setHeaders(Array.isArray(pa.dynamicHeader) ? pa.dynamicHeader : []);

      const list = Array.isArray(data.annualAttendnaceData)
        ? data.annualAttendnaceData
        : [];
      const mappedStudents = list.map((p) => ({
        upn: p.upn,
        name: p.forename,
        surname: p.surname,
        year: p.yearGroup,
        school: p.schoolname,
        pupilId: p.pupilId,
        attendanceList: Array.isArray(p.attendanceList)
          ? p.attendanceList
          : [],
      }));

      setStudents(mappedStudents);
      // do not reset navigation here to avoid double fetch loops
    } catch (error) {
      console.error("❌ Error details:", {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        config: error.config
      });
      
      if (error.code === 'ECONNABORTED') {
        setApiError("Request timeout. Please try again.");
      } else if (error.response?.status === 401) {
        setApiError("Session expired. Please login again.");
      } else if (error.response?.status === 404) {
        setApiError("API endpoint not found. Please check the URL.");
      } else if (error.response?.status >= 500) {
        setApiError("Server error. Please try again later.");
      } else if (error.response?.status === 400) {
        setApiError("Bad request. Please check your parameters.");
      } else if (!error.response) {
        setApiError("Network error. Please check your internet connection.");
      } else {
        setApiError(`Failed to load attendance data. Status: ${error.response?.status}`);
      }
    } finally {
      setApiLoading(false);
    }
  }, [page, recordsPerPage, debouncedFilters]);

  useEffect(() => {
    fetchAnnualAttendanceData();
  }, [fetchAnnualAttendanceData]);

  // Removed parent notify to avoid external loops that reset month view

  // ✅ Listen for month navigation events fired from filters to avoid filter-loop
  useEffect(() => {
    const handler = (e) => {
      const dir = e?.detail === 'Previous' ? 'Previous' : e?.detail === 'Next' ? 'Next' : null;
      if (dir) {
        fetchAnnualAttendanceData(dir);
      }
    };
    window.addEventListener('aa-month-nav', handler);
    return () => window.removeEventListener('aa-month-nav', handler);
  }, [fetchAnnualAttendanceData]);


  const saveAnnualAttendance = async ({ pupilId, masterMondayId, day, session, attendanceCode }) => {
    try {
      const userId = localStorage.getItem("userId");
      const roleId = localStorage.getItem("userRole");
      const schoolId = 423562;

      const dayMap = {
        mon: "monday", tue: "tuesday", wed: "wednesday",
        thu: "thursday", fri: "friday", sat: "saturday", sun: "sunday"
      };
      const fullDay = dayMap[day] || day;

      const response = await axios.post(
        `${settings.API_HOST}/rest/SaveAnnualAttendanceData`,
        null,
        {
          params: {
            "pupilAttendance.userId": userId,
            "pupilAttendance.schoolId": schoolId,
            "pupilAttendance.roleId": roleId,
            "pupilAttendance.pupilId": pupilId,
            "pupilAttendance.masterMondayId": masterMondayId,
            "pupilAttendance.day": fullDay,
            "pupilAttendance.session": session,
            "pupilAttendance.attendanceCode": attendanceCode,
          },
        }
      );

      console.log("✅ Attendance Saved:", response.data);
    } catch (error) {
      console.error("❌ Save Attendance Error:", error);
    }
  };

  const dates = useMemo(() => {
    if (!headers || headers.length === 0) return [];
    return headers.map((label) => {
      const parts = String(label).split(" ");
      const day = parts[0] || "";
      const date = parts[1] || "";
      return { day, date };
    });
  }, [headers]);

  const visibleStudents = useMemo(() => {
    let filtered = students.filter(s => (showLeavers ? true : !s.isLeaver));

    // Prefer immediate prop filters for UI responsiveness; fallback to debounced
    const yearGroupStr = (filters && filters.yearGroup) ? filters.yearGroup : debouncedFilters?.yearGroup;
    if (yearGroupStr && String(yearGroupStr).trim().length > 0) {
      const allowedYearGroups = new Set(
        String(yearGroupStr)
          .split(',')
          .map(y => y.trim().toLowerCase())
          .filter(Boolean)
      );
      filtered = filtered.filter(s =>
        allowedYearGroups.has(String(s.year || '').trim().toLowerCase())
      );
    }

    // Apply School filter locally (only when a school is actually selected)
    const schoolStr = (filters && typeof filters.subschoolId === 'string') ? filters.subschoolId : (debouncedFilters?.subschoolId || '');
    if (schoolStr && schoolStr.trim().length > 0) {
      const isNumericOnly = /^[0-9,\s]+$/.test(String(schoolStr));
      if (!isNumericOnly) {
        const allowedSchools = new Set(
          String(schoolStr)
            .split(',')
            .map(v => v.trim().toLowerCase())
            .filter(Boolean)
        );
        filtered = filtered.filter(s =>
          allowedSchools.has(String(s.school || '').trim().toLowerCase())
        );
      }
    }

    // Apply local search
    if (localSearch.trim()) {
      const search = localSearch.toLowerCase();
      filtered = filtered.filter(s =>
        s.upn?.toLowerCase().includes(search) ||
        s.name?.toLowerCase().includes(search) ||
        s.surname?.toLowerCase().includes(search) ||
        s.year?.toLowerCase().includes(search) ||
        s.school?.toLowerCase().includes(search)
      );
    }

    // Apply Attendance Code filter locally across all dates for each student
    const attendanceStr = (filters && filters.attendanceCode) ? filters.attendanceCode : debouncedFilters?.attendanceCode;
    if (attendanceStr && String(attendanceStr).trim().length > 0) {
      const allowedCodes = new Set(
        String(attendanceStr)
          .split(',')
          .map(v => v.trim().toLowerCase())
          .filter(Boolean)
      );

      const studentHasAllowedCode = (stu) => {
        const weeks = Array.isArray(stu.attendanceList) ? stu.attendanceList : [];
        for (let w = 0; w < weeks.length; w++) {
          const week = weeks[w] || {};
          const vals = [
            week.mondayAm, week.mondayPm,
            week.tuesdayAm, week.tuesdayPm,
            week.wednesdayAm, week.wednesdayPm,
            week.thursdayAm, week.thursdayPm,
            week.fridayAm, week.fridayPm,
          ].map(v => (v == null ? '' : String(v))).map(v => v.trim().toLowerCase());
          if (vals.some(v => allowedCodes.has(v))) return true;
        }
        return false;
      };

      filtered = filtered.filter(studentHasAllowedCode);
    }

    return filtered;
  }, [students, showLeavers, localSearch, filters, debouncedFilters]);

  const getMaxLength = (arr, selector) => {
    try {
      return arr.reduce((max, item) => {
        const value = String(selector(item) ?? '');
        return Math.max(max, value.length);
      }, 0);
    } catch (_) {
      return 0;
    }
  };

  const maxUpn = getMaxLength(visibleStudents, s => s.upn);
  const maxName = getMaxLength(visibleStudents, s => s.name);
  const maxSurname = getMaxLength(visibleStudents, s => s.surname);
  const maxYear = getMaxLength(visibleStudents, s => s.year);
  const maxSchool = getMaxLength(visibleStudents, s => s.school);

  const CH_TO_PX = 9;
  const computeWidthFromChars = (maxChars, minPx, paddingPx = 24) => {
    return Math.max(minPx, Math.ceil(maxChars * CH_TO_PX) + paddingPx);
  };

  const headerLabelLengths = {
    upn: 'UPN'.length,
    name: 'Name'.length,
    surname: 'Surname'.length,
    year: 'Year'.length,
    school: 'School'.length,
  };

  const colUpnPx = computeWidthFromChars(Math.max(10, maxUpn, headerLabelLengths.upn) + 2, 96);
  const colNamePx = computeWidthFromChars(Math.max(maxName, headerLabelLengths.name) + 2, 120);
  const colSurnamePx = computeWidthFromChars(Math.max(maxSurname, headerLabelLengths.surname) + 2, 120);
  const colYearPx = computeWidthFromChars(Math.max(maxYear, headerLabelLengths.year) + 2, 100);
  const colSchoolPx = computeWidthFromChars(Math.max(maxSchool, headerLabelLengths.school) + 2, 220, 32);

  const studentsInfoGridTemplate = `${colUpnPx}px ${colNamePx}px ${colSurnamePx}px ${colYearPx}px ${colSchoolPx}px`;
  const studentsInfoTotalWidthPx = colUpnPx + colNamePx + colSurnamePx + colYearPx + colSchoolPx;

  const scrollContainerRef = useRef(null);
  const trackRef = useRef(null);
  const [thumbWidthPct, setThumbWidthPct] = useState(0);
  const [thumbLeftPct, setThumbLeftPct] = useState(0);
  const DAY_GROUP_PX = 220;
  const AM_PM_PX = Math.floor(DAY_GROUP_PX / 2);
  const dayWidthRef = useRef(DAY_GROUP_PX);
  const [currentDayIndex, setCurrentDayIndex] = useState(0);

  const WEEK_BG_EVEN = '#fbfbfd';
  const WEEK_BG_ODD = '#f7f8fa';

  const recalcThumb = () => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const widthPct = (el.clientWidth / el.scrollWidth) * 100;
    const leftPct = (el.scrollLeft / el.scrollWidth) * 100;
    setThumbWidthPct(Math.max(5, Math.min(100, widthPct)));
    setThumbLeftPct(Math.max(0, Math.min(100, leftPct)));
  };

  useEffect(() => {
    recalcThumb();
    const el = scrollContainerRef.current;
    if (!el) return;

    let scrollEndTimer = null;
    const onScroll = () => {
      recalcThumb();
      if (scrollEndTimer) window.clearTimeout(scrollEndTimer);
      scrollEndTimer = window.setTimeout(() => {
        const width = dayWidthRef.current || DAY_GROUP_PX;
        const targetIndex = Math.round(el.scrollLeft / width);
        setCurrentDayIndex(targetIndex);
        el.scrollTo({ left: targetIndex * width, behavior: 'smooth' });
      }, 120);
    };
    const onResize = () => {
      recalcThumb();
      dayWidthRef.current = DAY_GROUP_PX;
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    return () => {
      el.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      if (scrollEndTimer) window.clearTimeout(scrollEndTimer);
    };
  }, []);

  const jumpToTrackPosition = (clientX) => {
    const el = scrollContainerRef.current;
    const track = trackRef.current;
    if (!el || !track) return;
    const rect = track.getBoundingClientRect();
    const ratio = (clientX - rect.left) / rect.width;
    const maxScroll = el.scrollWidth - el.clientWidth;
    const raw = maxScroll * Math.max(0, Math.min(1, ratio));
    const width = dayWidthRef.current || 1;
    const dayIndex = Math.round(raw / width);
    setCurrentDayIndex(dayIndex);
    el.scrollTo({ left: dayIndex * width, behavior: 'smooth' });
  };

  const goToDay = (index) => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const clamped = Math.max(0, Math.min(dates.length - 1, index));
    const width = dayWidthRef.current || 1;
    setCurrentDayIndex(clamped);
    el.scrollTo({ left: clamped * width, behavior: 'smooth' });
  };

  const prevDay = () => goToDay(currentDayIndex - 1);
  const nextDay = () => goToDay(currentDayIndex + 1);

  // When data loads after month nav, try to keep the first day of the month in view
  useEffect(() => {
    if (!scrollContainerRef.current) return;
    // after headers change, ensure index 0 visible
    goToDay(0);
  }, [headers]);

  const ATTENDANCE_CODES = [
    '(empty)', '/', 'B', 'K', 'L', 'P', 'V', 'W',
    '(empty)', 'C', 'C1', 'C2', 'E', 'I', 'J1', 'M', 'R', 'S',
    'T', '(empty)', 'G', 'N', 'O', 'U', '(empty)', 'D', 'Q', 'X',
    'Y1', 'Y2', 'Y3', 'Y4', 'Y5', 'Y6', 'Y7', 'Z', '\\', '#', '-'
  ];

  const attendanceOptions = ATTENDANCE_CODES.map(code => ({
    value: code === '(empty)' ? '' : code,
    label: code
  }));

  const [attendanceMap, setAttendanceMap] = useState({});
  const [editingCellKey, setEditingCellKey] = useState(null);

  const makeCellKey = (studentUpn, dateIndex, slot) => `${studentUpn}|${dateIndex}|${slot}`;

  const setAttendance = (student, dateIndex, slot, value) => {
    const key = makeCellKey(student.upn, dateIndex, slot);
    setAttendanceMap(prev => ({ ...prev, [key]: value }));

    const dayShort = dates[dateIndex]?.day?.toLowerCase();
    const dayMap = {
      mon: "monday", tue: "tuesday", wed: "wednesday",
      thu: "thursday", fri: "friday", sat: "saturday", sun: "sunday"
    };
    const fullDay = dayMap[dayShort] || dayShort;
    const session = slot.toUpperCase();
    const fieldName = `${fullDay}${session.charAt(0)}${session.slice(1).toLowerCase()}`;

    const attendanceRecord = student.attendanceList.find(
      (rec) => Object.prototype.hasOwnProperty.call(rec, fieldName)
    );

    const masterMondayId = attendanceRecord?.masterMondayId || 0;

    const valueToSave = mapToSavedValue(value);
    saveAnnualAttendance({
      pupilId: student.pupilId,
      masterMondayId,
      day: fullDay,
      session,
      attendanceCode: valueToSave,
    });
  };

  useEffect(() => {
    const map = {};
    const dayIndexToField = (indexInWeek) => {
      switch (indexInWeek) {
        case 0: return { am: 'mondayAm', pm: 'mondayPm' };
        case 1: return { am: 'tuesdayAm', pm: 'tuesdayPm' };
        case 2: return { am: 'wednesdayAm', pm: 'wednesdayPm' };
        case 3: return { am: 'thursdayAm', pm: 'thursdayPm' };
        case 4: return { am: 'fridayAm', pm: 'fridayPm' };
        default: return { am: null, pm: null };
      }
    };

    visibleStudents.forEach((stu) => {
      const weeks = Array.isArray(stu.attendanceList) ? stu.attendanceList : [];
      dates.forEach((_, dateIdx) => {
        const weekIdx = Math.floor(dateIdx / 5);
        const dayInWeek = dateIdx % 5;
        const fields = dayIndexToField(dayInWeek);
        const weekObj = weeks[weekIdx] || {};
        const amVal = fields.am ? weekObj[fields.am] : null;
        const pmVal = fields.pm ? weekObj[fields.pm] : null;
        const amDisp = amVal == null ? null : mapToDisplayValue(String(amVal));
        const pmDisp = pmVal == null ? null : mapToDisplayValue(String(pmVal));
        if (amDisp != null) map[makeCellKey(stu.upn, dateIdx, 'AM')] = amDisp;
        if (pmDisp != null) map[makeCellKey(stu.upn, dateIdx, 'PM')] = pmDisp;
      });
    });

    setAttendanceMap(prev => {
      const prevKeys = Object.keys(prev);
      const newKeys = Object.keys(map);
      if (prevKeys.length === newKeys.length && prevKeys.every(k => prev[k] === map[k])) {
        return prev;
      }
      return map;
    });
  }, [dates, visibleStudents]);

  // ✅ Server-side Export API Function
  const exportAttendanceData = async () => {
    try {
      setApiLoading(true);
      
      const userId = localStorage.getItem("userId");
      const roleId = localStorage.getItem("userRole");
      const schoolId = 423562;

      if (!userId || !roleId) {
        alert("User session expired. Please login again.");
        return;
      }

      const params = {
        "pupilAttendance.schoolId": schoolId,
        "pupilAttendance.userId": userId,
        "pupilAttendance.roleId": roleId,
        "pupilAttendance.academicYear": "2025-26",
        "pupilAttendance.leavers": debouncedFilters.leavers || "false",
        "pupilAttendance.attendanceCodes": debouncedFilters.attendanceCode || "",
        "pupilAttendance.careStatus": debouncedFilters.careStatus || "",
        "pupilAttendance.gender": debouncedFilters.gender || "",
        "pupilAttendance.localAuthority": debouncedFilters.localAuthority || "",
        "pupilAttendance.send": debouncedFilters.send || "",
        "pupilAttendance.eal": debouncedFilters.eal || "",
        "pupilAttendance.uasc": debouncedFilters.uasc || "",
        "pupilAttendance.subschoolId": debouncedFilters.subschoolId || "",
        "pupilAttendance.yearGroup": debouncedFilters.yearGroup || "",
      };

      console.log('📤 Export API Request:', params);

      const response = await axios.get(
        `${settings.API_HOST}/rest/exportAnnualAttendance`,
        { 
          params,
          timeout: 60000, // 60 second timeout for export
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );

      console.log('📥 Export API Response:', response.data);
      
      // Success message
      alert("Attendance data export request sent successfully! Excel file will be sent to your email shortly.");
      
    } catch (error) {
      console.error("❌ Export failed:", error);
      alert(`Export failed: ${error.response?.data?.message || error.message}`);
    } finally {
      setApiLoading(false);
    }
  };

  useEffect(() => {
    const handleExport = () => {
      exportAttendanceData();
    };

    window.addEventListener('aa-export-attendance', handleExport);
    return () => window.removeEventListener('aa-export-attendance', handleExport);
  }, [debouncedFilters]);

  if (apiLoading && students.length === 0) {
    return <div className="aa-card" style={{ padding: '40px', textAlign: 'center' }}>Loading...</div>;
  }

  if (apiError) {
    return <div className="aa-card" style={{ padding: '40px', textAlign: 'center', color: '#dc2626' }}>{apiError}</div>;
  }

  return (
    <div className="aa-card">
      <div className="aa-row" style={{ justifyContent: 'space-between', marginBottom: '16px' }}>
        <div className="aa-row" style={{ gap: '8px' }}>
          <span className="aa-label">Show</span>
          <Select value={recordsPerPage.toString()} onValueChange={(val) => { setRecordsPerPage(Number(val)); setPage(1); }}>
            <SelectTrigger className="aa-select-trigger" style={{ minWidth: '80px' }}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="10">10</SelectItem>
              <SelectItem value="25">25</SelectItem>
              <SelectItem value="50">50</SelectItem>
              <SelectItem value="100">100</SelectItem>
            </SelectContent>
          </Select>
          <span className="aa-label">entries</span>
        </div>

        <div className="aa-row" style={{ gap: '8px' }}>
          <span className="aa-label">Search:</span>
          <Input type="text" className="aa-input" style={{ width: '192px' }} placeholder="" value={localSearch} onChange={(e) => setLocalSearch(e.target.value)} />
        </div>
      </div>

      <div className="aa-table-shell">
        <div className="aa-table-wrap" ref={scrollContainerRef}>
          <table className="aa-table">
            <thead>
              <tr>
                <th className="aa-students-sticky aa-th-date" style={{ width: `${studentsInfoTotalWidthPx}px`, minWidth: `${studentsInfoTotalWidthPx}px`, background: '#fff', borderRight: '1px solid var(--aa-gray-300)', boxShadow: '6px 0 12px -8px rgba(0,0,0,0.18)', zIndex: 100 }}>
                  Students Info
                </th>
                {dates.map((date, index) => {
                  const weekBg = Math.floor(index / 7) % 2 === 0 ? WEEK_BG_EVEN : WEEK_BG_ODD;
                  return (
                    <th key={index} colSpan={2} className="aa-th" style={{ width: `${DAY_GROUP_PX}px`, minWidth: `${DAY_GROUP_PX}px`, background: weekBg }}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <span>{date.day}</span>
                        <span>{date.date}</span>
                      </div>
                    </th>
                  );
                })}
              </tr>

              <tr>
                <th className="aa-students-sticky p-0" style={{ width: `${studentsInfoTotalWidthPx}px`, minWidth: `${studentsInfoTotalWidthPx}px`, background: '#fff', borderRight: '1px solid var(--aa-gray-300)', boxShadow: '6px 0 12px -8px rgba(0,0,0,0.16)', zIndex: 100 }}>
                  <div className="aa-grid-xs" style={{ gridTemplateColumns: studentsInfoGridTemplate }}>
                    <div className="aa-cell" style={{ borderRight: '1px solid var(--aa-gray-300)', fontWeight: 500, textAlign: 'left' }}>UPN</div>
                    <div className="aa-cell" style={{ borderRight: '1px solid var(--aa-gray-300)', fontWeight: 500, textAlign: 'left' }}>Name</div>
                    <div className="aa-cell" style={{ borderRight: '1px solid var(--aa-gray-300)', fontWeight: 500, textAlign: 'left' }}>Surname</div>
                    <div className="aa-cell" style={{ borderRight: '1px solid var(--aa-gray-300)', fontWeight: 500, textAlign: 'left' }}>Year</div>
                    <div className="aa-cell" style={{ fontWeight: 500, textAlign: 'left' }}>School</div>
                  </div>
                </th>
                {dates.map((_, index) => {
                  const weekBg = Math.floor(index / 7) % 2 === 0 ? WEEK_BG_EVEN : WEEK_BG_ODD;
                  return (
                    <React.Fragment key={index}>
                      <th data-day-index={index} data-day-slot="AM" className="aa-th" style={{ width: `${AM_PM_PX}px`, minWidth: `${AM_PM_PX}px`, background: weekBg }}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          <span>AM</span>
                          <div className="aa-dot"></div>
                        </div>
                      </th>
                      <th data-day-index={index} data-day-slot="PM" className="aa-th" style={{ width: `${AM_PM_PX}px`, minWidth: `${AM_PM_PX}px`, background: weekBg }}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          <span>PM</span>
                          <div className="aa-dot"></div>
                        </div>
                      </th>
                    </React.Fragment>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {visibleStudents.map((s) => (
                <tr key={s.upn} className="aa-row-alt">
                  <td className="aa-students-sticky-body p-0" style={{ background: '#fff', borderRight: '1px solid var(--aa-gray-300)', boxShadow: '6px 0 12px -8px rgba(0,0,0,0.12)', zIndex: 95 }}>
                    <div className="aa-grid-xs" style={{ gridTemplateColumns: studentsInfoGridTemplate }}>
                      <div className="aa-cell" style={{ borderRight: '1px solid var(--aa-gray-200)', textAlign: 'left', padding: '8px 12px', whiteSpace: 'nowrap' }} title={s.upn}>{s.upn}</div>
                      <div className="aa-cell" style={{ borderRight: '1px solid var(--aa-gray-200)', textAlign: 'left', padding: '8px 12px', whiteSpace: 'nowrap' }} title={s.name}>{s.name}</div>
                      <div className="aa-cell" style={{ borderRight: '1px solid var(--aa-gray-200)', textAlign: 'left', padding: '8px 12px', whiteSpace: 'nowrap' }} title={s.surname}>{s.surname}</div>
                      <div className="aa-cell" style={{ borderRight: '1px solid var(--aa-gray-200)', textAlign: 'left', padding: '8px 12px', whiteSpace: 'nowrap' }} title={s.year}>{s.year}</div>
                      <div className="aa-cell" style={{ textAlign: 'left', padding: '8px 12px', whiteSpace: 'nowrap' }} title={s.school}>
                        <span>{s.school}</span>
                        {s.isLeaver && (
                          <span style={{ marginLeft: '8px', display: 'inline-block', borderRadius: '9999px', background: '#fee2e2', color: '#b91c1c', padding: '2px 8px', fontSize: '10px', verticalAlign: 'middle' }}>Leaver</span>
                        )}
                      </div>
                    </div>
                  </td>
                  {dates.map((_, colIdx) => {
                    const weekBg = Math.floor(colIdx / 7) % 2 === 0 ? WEEK_BG_EVEN : WEEK_BG_ODD;
                    return (
                      <React.Fragment key={`${s.upn}-${colIdx}`}>
                        <td className="aa-td" style={{ width: `${AM_PM_PX}px`, minWidth: `${AM_PM_PX}px`, height: '48px', background: weekBg }}>
                          <div style={{ padding: '8px 12px' }}>
                            {(() => {
                              const key = makeCellKey(s.upn, colIdx, 'AM');
                              const value = attendanceMap[key] || '';
                              const isEditing = editingCellKey === key;
                              return (
                                <div className="relative">
                                  {!isEditing ? (
                                    <button type="button" className="aa-cell-pill" onClick={() => setEditingCellKey(key)}>{value || ''}</button>
                                  ) : (
                                    <select autoFocus className="aa-select-inline" value={attendanceMap[makeCellKey(s.upn, colIdx, 'AM')] || ''} onChange={(e) => setAttendance(s, colIdx, 'AM', e.target.value)} onBlur={() => setEditingCellKey(null)}>
                                      <option value="" disabled hidden>Select...</option>
                                      {attendanceOptions.map((opt, idx) => (
                                        <option key={idx} value={opt.value}>{opt.label}</option>
                                      ))}
                                    </select>
                                  )}
                                </div>
                              );
                            })()}
                          </div>
                        </td>
                        <td className="aa-td" style={{ width: `${AM_PM_PX}px`, minWidth: `${AM_PM_PX}px`, height: '48px', background: weekBg }}>
                          <div style={{ padding: '8px 12px' }}>
                            {(() => {
                              const key = makeCellKey(s.upn, colIdx, 'PM');
                              const value = attendanceMap[key] || '';
                              const isEditing = editingCellKey === key;
                              return (
                                <div className="relative">
                                  {!isEditing ? (
                                    <button type="button" className="aa-cell-pill" onClick={() => setEditingCellKey(key)}>{value || ''}</button>
                                  ) : (
                                    <select autoFocus className="aa-select-inline" value={attendanceMap[makeCellKey(s.upn, colIdx, 'PM')] || ''} onChange={(e) => setAttendance(s, colIdx, 'PM', e.target.value)} onBlur={() => setEditingCellKey(null)}>
                                      <option value="" disabled hidden>Select...</option>
                                      {attendanceOptions.map((opt, idx) => (
                                        <option key={idx} value={opt.value}>{opt.label}</option>
                                      ))}
                                    </select>
                                  )}
                                </div>
                              );
                            })()}
                          </div>
                        </td>
                      </React.Fragment>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="aa-hscroll">
          <div className="aa-hscroll-row">
            <Button variant="ghost" size="sm" className="aa-btn aa-btn-ghost aa-btn-sm" onClick={prevDay}>
              <ChevronLeft className="aa-icon-md" />
            </Button>
            <div ref={trackRef} className="aa-track" style={{ marginLeft: `${studentsInfoTotalWidthPx}px` }} onClick={(e) => jumpToTrackPosition(e.clientX)}>
              <div className="aa-thumb" style={{ width: `${thumbWidthPct}%`, transform: `translateX(${thumbLeftPct}%)` }}></div>
            </div>
            <Button variant="ghost" size="sm" className="aa-btn aa-btn-ghost aa-btn-sm" onClick={nextDay}>
              <ChevronRight className="aa-icon-md" />
            </Button>
          </div>
        </div>
      </div>

      <div className="aa-footer">
        <span className="aa-footer-text">
          {totalRecords > 0
            ? `Showing ${(page - 1) * recordsPerPage + 1} to ${Math.min(page * recordsPerPage, totalRecords)} of ${totalRecords} entries`
            : "Showing 0 to 0 of 0 entries"}
        </span>

        <div className="aa-row" style={{ gap: '8px' }}>
          <Button variant="outline" size="sm" className="aa-btn aa-btn-outline aa-btn-sm" disabled={page <= 1} onClick={() => setPage((prev) => Math.max(prev - 1, 1))}>
            Previous
          </Button>

          <span className="aa-footer-text">
            Page {page} of {totalPages}
          </span>

          <Button variant="outline" size="sm" className="aa-btn aa-btn-outline aa-btn-sm" disabled={page >= totalPages} onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}>
            Next
          </Button>
        </div>
      </div>
    </div>
  );
};

// PropTypes for type checking
AttendanceTable.propTypes = {
  showLeavers: PropTypes.bool,
  filters: PropTypes.object,
  onMonthNavigation: PropTypes.func,
};

export default AttendanceTable;